import { formatDate, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const monthYear = (date) => (date?.Year ? `${date.Month ? `${String(date.Month).padStart(2, '0')}/` : ''}${date.Year}` : '')
const seen = (from, to) => [monthYear(from), monthYear(to)].filter(Boolean).filter((v, i, all) => all.indexOf(v) === i).join(' to ')

export const companyStanding = (value) => {
    const status = upper(value)
    if (!status) return 'unknown'
    if (/(?<!IN)VOLUNTAR/.test(status)) return 'inactive'
    if (/DISSOLV|FORFEIT|SUSPEN|REVOK|NOT IN GOOD|NONCOMPLIANCE|DELINQ|DEFAULT|PAST DUE|ON NOTICE|INTENT TO DISSOLVE/.test(status)) return 'bad'
    if (/INACTIVE|WITHDRAWN|EXPIRED|CONVERTED|MERGED|TERMINAT|CANCEL|PRIOR|CLOSED/.test(status)) return 'inactive'
    if (/ACTIVE|GOOD STANDING|IN EXISTENCE|IN USE|CURRENT|EXISTS|REINSTATED/.test(status)) return 'active'
    return 'unknown'
}
const STANDING = { bad: 3, unknown: 2, active: 1, inactive: 0 }

export function buildBusinessTies(person, now = new Date()) {
    const companies = new Map()
    list(person?.CorporateAffiliations?.Affiliation).filter(a => a?.CompanyName).forEach(a => {
        const key = `${upper(a.CompanyName).replace(/[^A-Z0-9]/g, '')}|${upper(a.State)}`
        const entry = companies.get(key) || { name: titleCase(a.CompanyName), state: text(a.State), titles: new Set(), records: [] }
        list(text(a.Title).split(/,\s*/)).map(t => titleCase(t.trim())).filter(Boolean).forEach(t => entry.titles.add(t))
        entry.records.push(a)
        companies.set(key, entry)
    })
    const roles = [...companies.values()].map(c => {
        const latest = [...c.records].sort((x, y) => (dateValue(y.RecordDate) || dateValue(y.FilingDate)) - (dateValue(x.RecordDate) || dateValue(x.FilingDate)))[0]
        const status = text(latest.StatusDescription || latest.Status)
        const standing = companyStanding(status)
        const filed = c.records.map(r => r.FilingDate).filter(d => dateValue(d)).sort((x, y) => dateValue(x) - dateValue(y))[0]
        const statusAt = dateValue(latest.RecordDate) || dateValue(latest.FilingDate)
        return {
            more: moreFromLexis(c.records, 'CorporateAffiliations/Affiliation[]'),
            standing, rank: STANDING[standing], filedAt: dateValue(filed),
            recentBad: standing === 'bad' && statusAt > 0 && (now.getTime() - statusAt) / (365.25 * 86400000) <= 3,
            title: [c.name, c.state].filter(Boolean).join(' · '),
            facts: [[...c.titles].join(', '), filed && `Filed ${formatDate(filed)}`].filter(Boolean).join(' · '),
            chips: [status && { label: titleCase(status), tone: standing === 'bad' ? 'declined' : 'neutral' }].filter(Boolean),
            details: [
                ['Status', [titleCase(status), latest.RecordDate && `as of ${formatDate(latest.RecordDate)}`].filter(Boolean).join(' ')],
                ['Roles', [...c.titles].join(', ')],
                ['Corporation number', [...new Set(c.records.map(r => text(r.CorporationNumber)).filter(Boolean))].join(', ')],
                ['Filed', formatDate(filed)],
                ['For profit', upper(latest.ForProfit) === 'Y' ? 'Yes' : upper(latest.ForProfit) === 'N' ? 'No' : ''],
                ['Record type', titleCase(latest.RecordType)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    }).sort((a, b) => b.rank - a.rank || b.filedAt - a.filedAt)

    const workplaces = list(person?.PeopleAtWorks?.PeopleAtWork).filter(w => w?.CompanyName)
        .filter((w, i, all) => all.findIndex(x => upper(x.CompanyName) === upper(w.CompanyName) && upper(x.Title) === upper(w.Title)) === i)
        .map(w => ({
            lastSeen: dateValue(w.DateLastSeen) || dateValue(w.DateFirstSeen),
            title: titleCase(w.CompanyName),
            facts: [titleCase(w.Title), seen(w.DateFirstSeen, w.DateLastSeen) && `Seen ${seen(w.DateFirstSeen, w.DateLastSeen)}`,
                [titleCase(w.Address?.City), w.Address?.State].filter(Boolean).join(', ')].filter(Boolean).join(' · '),
            chips: [],
            details: [['Department', titleCase(w.Department)]].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        })).sort((a, b) => b.lastSeen - a.lastSeen)

    const assumedNames = list(person?.FictitiousBusinesses?.FictitiousBusiness).filter(f => f?.Business?.Name).map(f => {
        const b = f.Business
        const expired = dateValue(f.FilingExpirationDate) && dateValue(f.FilingExpirationDate) < Date.now()
        const cancelled = dateValue(f.FilingCancellationDate)
        return {
            more: moreFromLexis(f, 'FictitiousBusinesses/FictitiousBusiness[]'),
            filedAt: dateValue(f.FilingDate),
            title: [titleCase(b.Name), f.FilingJurisdiction].filter(Boolean).join(' · '),
            facts: [titleCase(f.FilingType), f.FilingDate && `Filed ${formatDate(f.FilingDate)}`,
                f.FilingExpirationDate && `${expired ? 'Expired' : 'Expires'} ${formatDate(f.FilingExpirationDate)}`].filter(Boolean).join(' · '),
            chips: [cancelled ? { label: 'Cancelled', tone: 'neutral' } : expired ? { label: 'Expired', tone: 'neutral' } : null].filter(Boolean),
            details: [
                ['Business type', titleCase(b.TypeDescription)],
                ['Status', titleCase(b.Status)],
                ['Started', formatDate(b.StartDate)],
                ['Filing number', text(f.FilingNumber)],
                ['Cancelled', formatDate(f.FilingCancellationDate)],
                ['Where', [titleCase(b.OfficeAddress?.City || b.MailingAddress?.City), b.OfficeAddress?.State || b.MailingAddress?.State].filter(Boolean).join(', ')],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    }).sort((a, b) => b.filedAt - a.filedAt)

    return {
        roles, workplaces, assumedNames,
        summary: {
            companies: roles.length,
            bad: roles.filter(r => r.standing === 'bad').length,
            badRecent: roles.filter(r => r.recentBad).length,
            active: roles.filter(r => r.standing === 'active').length,
        },
        hasData: roles.length + workplaces.length + assumedNames.length > 0,
    }
}
